import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

type FormModalProps = {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  maxWidthClassName?: string;
  ariaLabel?: string;
};

export function FormModal({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  maxWidthClassName = 'max-w-md',
  ariaLabel,
}: FormModalProps) {
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="form-modal-title"
        aria-label={ariaLabel ?? title}
        className={`flex max-h-[min(92vh,920px)] w-full flex-col border border-ha-border bg-ha-card ${maxWidthClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-ha-border px-6 py-4">
          <div className="min-w-0">
            <h2 id="form-modal-title" className="text-lg font-bold text-ha-text">
              {title}
            </h2>
            {subtitle ? <p className="mt-0.5 text-xs text-ha-subtle">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center border border-ha-border2 bg-ha-card2 text-ha-muted transition hover:border-ha-border hover:text-ha-text"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">{children}</div>

        {footer ? (
          <div className="flex shrink-0 justify-end gap-2 border-t border-ha-border px-6 py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ModalButtonSecondary({
  children,
  onClick,
  type = 'button',
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="rounded-element border border-ha-border2 px-4 py-2 text-xs font-bold uppercase text-ha-subtle transition hover:border-ha-border hover:text-ha-text disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function ModalButtonPrimary({
  children,
  onClick,
  type = 'button',
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className="rounded-element px-4 py-2 text-xs font-bold uppercase text-black disabled:opacity-50"
      style={{ background: '#ffc800' }}
    >
      {children}
    </button>
  );
}
